import { BaseRequest, BaseRequestConfig } from './base';
import axios, { AxiosInstance, AxiosResponse, AxiosRequestConfig, AxiosProgressEvent } from 'axios';
import { message } from 'antd';
import { ErrorCode, handleErrorMessage } from '@/utils/request/errorCode';
import authStorage from '../storage/authStorage';
import { TIME } from '@/config/constants';
import { t } from 'i18next';
export class AxiosRequest extends BaseRequest {
  private instance: AxiosInstance;

  constructor(config?: BaseRequestConfig) {
    super(config);
    this.instance = axios.create({
      baseURL: this.baseURL,
      timeout: this.defaultConfig.timeout,
      headers: {
        'Content-Type': 'application/json;charset=utf-8', // <-- add default JSON header here
        ...this.defaultConfig.headers,
      }
    });

    this.setupInterceptors();
  }

  private setupInterceptors() {
    // 请求拦截器
    this.instance.interceptors.request.use(
      async (config: any) => {
        
        const token = authStorage.getToken();
        if (token) {
          // headers['Authorization'] = `Bearer ${token}`;
          // // 添加 refresh token
          // const refreshToken = authStorage.getRefreshToken();
          // if (refreshToken) {
          //   headers['X-Refresh-Token'] = refreshToken;
          // }
          config.headers = config.headers || {};
          config.headers['Token'] = token;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // 响应拦截器
    this.instance.interceptors.response.use(
      (response: AxiosResponse) => {
        // 处理自动刷新的token
        const newToken = response.headers['x-access-token'];
        const newRefreshToken = response.headers['x-refresh-token'];
        
        if (newToken && newRefreshToken) {
          authStorage.setTokenInfo({
            token: newToken,
            refreshToken: newRefreshToken,
            expiresIn: TIME.TOKEN_EXPIRE,
            remember: authStorage.getStorageType() === 'local'
          });
        }

        if (response.config.responseType === 'blob') {
          return response;
        }
        
        if (response.data.ret !== ErrorCode.SUCCESS) {
          const bizError = new Error(handleErrorMessage(response.data.ret, response.data.msg));
          (bizError as any).isBusinessError = true;
          (bizError as any).ret = response.data.ret;
          return Promise.reject(bizError);
        }
        
        return response.data;
      },
      (error) => Promise.reject(error)
    );
  }
  private toAxiosConfig(config?: BaseRequestConfig): AxiosRequestConfig {
    if (!config) return {};
    const { onUploadProgress, onDownloadProgress, ...rest } = config;
    return {
      ...rest,
      onUploadProgress: onUploadProgress && ((e: AxiosProgressEvent) => {
        const total = e.total || 0;
        onUploadProgress(total ? e.loaded / total : 0);
      }),
      onDownloadProgress: onDownloadProgress && ((e: AxiosProgressEvent) => {
        const total = e.total || 0;
        onDownloadProgress(total ? e.loaded / total : 0);
      })
    };
  }
  // 发送请求
  private async request<T>(config: AxiosRequestConfig & { retry?: number, retryDelay?: number, loading?: boolean }): Promise<T> {
    const { retry, retryDelay, loading, ...axiosConfig } = config;
    // console.log('axiosConfig', axiosConfig);
    const endProgress = this.handleProgress(loading);
      // 处理超时
      const controller = new AbortController();
      const timeout = config.timeout || this.defaultConfig.timeout;
      const timeoutId = setTimeout(() => controller.abort(), timeout);
      // console.log('timeoutId', timeout);
      const finalConfig = {
        ...axiosConfig,
        signal: controller.signal
      };
    try {
      if (retry && retry > 0) {
        return await this.retryRequest(async () => {
          const { data } = await this.instance.request<T>(finalConfig);
          clearTimeout(timeoutId);

          return data;
        }, retry, retryDelay);
      }

      const { data } = await this.instance.request<T>(finalConfig);
      clearTimeout(timeoutId);

      return data;
    } catch (error) {
      return this.handleError(error);
    } finally {
      endProgress();
    }
  }

  // GET 请求
  async get<T>(url: string, config?: BaseRequestConfig): Promise<T> {
    const wrappedData = this.generateSignedBody(config?.data); // <-- wrap here
    return this.request<T>({
      ...this.toAxiosConfig(config),
      method: 'GET',
      url,
      params: wrappedData
    });
  }

  // POST 请求
  async post<T>(url: string, data?: any, config?: BaseRequestConfig): Promise<T> {
    const wrappedData = this.generateSignedBody(data);  // <-- wrap here
    return this.request<T>({
      ...this.toAxiosConfig(config),
      method: 'POST',
      url,
      data: wrappedData,
      
      headers: {
        'Content-Type': 'application/json;charset=utf-8',
        ...(config?.headers || {})
      }
    });
  }

  // PUT 请求
  async put<T>(url: string, data?: any, config?: BaseRequestConfig): Promise<T> {
    const wrappedData = this.generateSignedBody(data); // <-- wrap here
    return this.request<T>({
      ...this.toAxiosConfig(config),
      method: 'PUT',
      url,
      data: wrappedData
    });
  }

  // DELETE 请求
  async delete<T>(url: string, config?: BaseRequestConfig): Promise<T> {
    const wrappedData = this.generateSignedBody(config?.data); // <-- wrap here
    return this.request<T>({
      ...this.toAxiosConfig(config),
      method: 'DELETE',
      url,
      data: wrappedData
    });
  }

  // 上传文件
  async upload<T>(url: string, file: File | Blob | FormData, config?: BaseRequestConfig): Promise<T> {
    let formData: FormData;
    
    if (file instanceof FormData) {
      formData = file;
    } else {
      formData = new FormData();
      formData.append('file', file);
    }

    const wrappedData = this.generateSignedBody(formData); // <-- wrap here

    return this.request<T>({
      method: 'POST',
      url,
      data: wrappedData,
      headers: {
        'Content-Type': 'multipart/form-data',
        ...config?.headers
      }
    });
  }

  // 下载文件
  async download(url: string, fileName?: string, config?: BaseRequestConfig): Promise<void> {
    const wrappedData = this.generateSignedBody(config?.data); // <-- wrap here
    const response = await this.request<Blob>({
      ...this.toAxiosConfig(config),
      method: 'GET',
      url,
      responseType: 'blob',
      data: wrappedData
    });

    // 处理下载
    const blob = new Blob([response], { type: 'application/octet-stream' });
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = fileName || 'download';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);
  }

  protected handleError(error: any) {
    if (error.name === 'AbortError' || error.name === 'CanceledError' || error.code === 'ECONNABORTED') {
      const msg = t('network.request-canceled-or-timeout');
      message.error(msg);
      return Promise.reject(new Error(msg));
    }

    if (error.isBusinessError) {
      message.error(error.message);
      return Promise.reject(error);
    }

    if (error.response) {
      const status = error.response.status;
      // Try to get error message from response.data.message or fallback to status text
      const serverMessage = error.response.data?.message || error.response.data?.msg || handleErrorMessage(status) || error.message;
      message.error(serverMessage);
      return Promise.reject(new Error(serverMessage));
    } else {
      const msg = t('network.network-error');
      message.error(msg);
      return Promise.reject(new Error(msg));
    }
  }  
}

export default new AxiosRequest();